import React, { Fragment, useMemo, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const normalizeCategory = (category = {}) => ({
  ...category,
  id: Number(getSalesId(category) || 0),
  code: String(getSalesValue(category, 'code', 'Code') || ''),
  name: String(getSalesValue(category, 'name', 'Name') || ''),
  description: String(getSalesValue(category, 'description', 'Description') || ''),
  isActive: Boolean(getSalesValue(category, 'isActive', 'IsActive') ?? true),
  isUsed: Boolean(getSalesValue(category, 'isUsed', 'IsUsed') ?? false),
});

const normalizePart = (part = {}) => ({
  ...part,
  id: Number(getSalesId(part) || 0),
  categoryId: Number(getSalesValue(part, 'categoryId', 'CategoryId') || 0),
  partNumber: String(getSalesValue(part, 'partNumber', 'PartNumber') || ''),
  name: String(getSalesValue(part, 'name', 'Name') || ''),
  sortOrder: Number(getSalesValue(part, 'sortOrder', 'SortOrder') || 0),
  isUsed: Boolean(getSalesValue(part, 'isUsed', 'IsUsed') ?? false),
});

const normalizeCategories = (rows) => rows.map(normalizeCategory);
const normalizeParts = (rows) => rows.map(normalizePart);

const PartsList = () => {
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [editingSortId, setEditingSortId] = useState(null);
  const [editSortValue, setEditSortValue] = useState(0);
  const [actionError, setActionError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const fileInputRef = useRef(null);

  const {
    items: categories,
    setItems: setCategories,
    loading: categoriesLoading,
    error: categoriesError,
    request,
    endpoints,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.categories.base, {
    normalize: normalizeCategories,
    errorMessage: 'Unable to load categories.',
  });

  const {
    items: parts,
    setItems: setParts,
    loading: partsLoading,
    error: partsError,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.parts.base, {
    normalize: normalizeParts,
    errorMessage: 'Unable to load parts.',
  });

  const partsByCategoryId = useMemo(() => {
    const grouped = new Map();
    parts.forEach((part) => {
      const current = grouped.get(part.categoryId) || [];
      current.push(part);
      grouped.set(part.categoryId, current);
    });
    grouped.forEach((values) => values.sort((left, right) => left.sortOrder - right.sortOrder || left.partNumber.localeCompare(right.partNumber)));
    return grouped;
  }, [parts]);

  const getCategoryParts = (categoryId) => partsByCategoryId.get(categoryId) || [];

  const toggleExpand = (id) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleActiveToggle = async (category) => {
    if (!canEditSales) return;
    const payload = { ...category, Id: category.id, IsActive: !category.isActive };
    setSavingId(category.id);
    setActionError('');

    try {
      await request({ method: 'PUT', url: endpoints.categories.byId(category.id), data: payload });
      setCategories((current) => current.map((item) => (item.id === category.id ? normalizeCategory(payload) : item)));
    } catch {
      setActionError('Unable to update category.');
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveSortOrder = async (part) => {
    const payload = { ...part, Id: part.id, SortOrder: editSortValue };
    setSavingId(part.id);
    setActionError('');

    try {
      await request({ method: 'PUT', url: endpoints.parts.byId(part.id), data: payload });
      setParts((current) => current.map((item) => (item.id === part.id ? normalizePart(payload) : item)));
      setEditingSortId(null);
    } catch {
      setActionError('Unable to save part sort order.');
    } finally {
      setSavingId(null);
    }
  };

  const loading = categoriesLoading || partsLoading;

  return (
    <section className="sales-portal-page" aria-labelledby="sales-parts-title">
      <Row className="mb-3">
        <Col><h4 id="sales-parts-title">Instrument List</h4></Col>
      </Row>

      <Row className="mb-3">
        <Col className="d-flex gap-3">
          <Button className="btn-success addbtnview" disabled={!canEditSales}>
            <i className="bi bi-plus-circle me-2"></i>
            Add Category
          </Button>
          <Button
            variant="primary"
            disabled={!canEditSales}
            style={{ minHeight: '45px', minWidth: '200px', borderRadius: 0, textTransform: 'uppercase', fontWeight: 500 }}
            onClick={() => fileInputRef.current?.click()}
          >
            Import Part Prices
          </Button>
          <input type="file" ref={fileInputRef} onChange={(event) => { event.target.value = ''; }} hidden />
        </Col>
      </Row>

      {(categoriesError || partsError || actionError) && <Alert variant="danger">{categoriesError || partsError || actionError}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr className="table-border">
                  <th style={{ width: '3%' }}></th>
                  <th style={{ width: '10%' }}>Code</th>
                  <th style={{ width: '68%' }}>Category Name</th>
                  <th style={{ width: '10%' }}>Active</th>
                  <th style={{ width: '9%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading parts...</div></td></tr>
                ) : categories.length === 0 ? (
                  <tr><td colSpan={5}><div className="sales-no-data">No categories found.</div></td></tr>
                ) : categories.map((category) => {
                  const isExpanded = expandedIds.has(category.id);
                  const categoryParts = getCategoryParts(category.id);
                  return (
                    <Fragment key={category.id}>
                      <tr className="table-border">
                        <td>
                          <button type="button" className="edit-icon" onClick={() => toggleExpand(category.id)} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.name}`}>
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                          </button>
                        </td>
                        <td>{category.code}</td>
                        <td title={category.description}>{category.name}</td>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={category.isActive}
                            disabled={!canEditSales || savingId === category.id}
                            onChange={() => handleActiveToggle(category)}
                            style={{ marginLeft: '25px' }}
                          />
                        </td>
                        <td>
                          {!category.isUsed && (
                            <button type="button" className="edit-icon" title="Edit" disabled={!canEditSales} style={{ marginLeft: '15px' }}>
                              <i className="bi bi-pencil-square"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={5} style={{ padding: '0 0 0 30px', background: '#f8f9fa' }}>
                            <Table size="sm" className="mb-0" style={{ width: '75%' }}>
                              <thead>
                                <tr className="table-border">
                                  <th style={{ width: '150px' }}>Sort Order</th>
                                  <th style={{ width: '200px' }}>Part Number</th>
                                  <th>Name</th>
                                  <th style={{ width: '80px' }}>Used</th>
                                </tr>
                              </thead>
                              <tbody>
                                {categoryParts.length === 0 ? (
                                  <tr><td colSpan={4}><div className="sales-no-data">No Parts found.</div></td></tr>
                                ) : (
                                  categoryParts.map((part) => (
                                    <tr key={part.id} className="table-border">
                                      <td>
                                        {editingSortId === part.id ? (
                                          <div className="d-flex align-items-center gap-1">
                                            <Form.Control
                                              type="text"
                                              size="sm"
                                              value={editSortValue}
                                              onChange={(event) => setEditSortValue(parseInt(event.target.value, 10) || 0)}
                                              style={{ width: '70px' }}
                                            />
                                            <button type="button" className="edit-icon" title="Save" disabled={savingId === part.id} onClick={() => handleSaveSortOrder(part)}>
                                              <i className="bi bi-save"></i>
                                            </button>
                                          </div>
                                        ) : (
                                          <span>
                                            {part.sortOrder}
                                            <button
                                              type="button"
                                              className="edit-icon"
                                              title="Edit"
                                              disabled={!canEditSales}
                                              onClick={() => {
                                                setEditingSortId(part.id);
                                                setEditSortValue(part.sortOrder);
                                              }}
                                              style={{ marginLeft: '15px' }}
                                            >
                                              <i className="bi bi-pencil-square"></i>
                                            </button>
                                          </span>
                                        )}
                                      </td>
                                      <td title={part.partNumber}>{part.partNumber}</td>
                                      <td title={part.name}>{part.name}</td>
                                      <td>
                                        {part.isUsed && <i className="bi bi-check-circle-fill text-success" style={{ marginLeft: '25px' }} title="Part is used"></i>}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartsList;
